import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../../lib/api';
import { useToast } from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useT } from '../../i18n';
import CommentEditor, { canEditComment } from './CommentEditor';
import { CommentText, CommentAttachments, commentMentions } from './CommentContent';
import './comments.css';

/** A deleted comment stays on the timeline as a stub; only the deletion stamp remains. */
export function isCommentDeleted(activity) {
  return !!activity?.metadata?.deletedAt;
}

/**
 * Deep-link support: notification links carry `#c-{activityId}`; when the activity feed
 * containing that entry renders, scroll it into view. Returns the id to highlight (or null).
 */
export function useCommentAnchor(activity) {
  const { hash } = useLocation();
  const target = hash?.startsWith('#c-') ? hash.slice(3) : null;

  useEffect(() => {
    if (!target || !activity?.length) return;
    if (!activity.some((a) => a.id === target)) return;
    // Let the timeline commit before scrolling.
    const frame = requestAnimationFrame(() => {
      document.getElementById(`c-${target}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    return () => cancelAnimationFrame(frame);
  }, [target, activity]);

  return target;
}

/** Author line under a comment: name, edited marker, and Edit / Delete for one's own recent comments. */
function CommentByline({ activity, apiBasePath, currentUserId, onEdit, onChanged }) {
  const t = useT();
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const editable = canEditComment(activity, currentUserId);

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.del(`${apiBasePath}/comments/${activity.id}`);
      onChanged?.();
    } catch (err) {
      toast.error(t('Could not delete comment'), { description: err?.message });
      setDeleting(false);
      setConfirming(false);
    }
  };

  return (
    <span className="pp-comment-byline">
      {activity.actorName ? t('by {name}', { name: activity.actorName }) : null}
      {activity.metadata?.editedAt && <span className="pp-comment-edited">{t('(edited)')}</span>}
      {editable && !confirming && (
        <>
          <button type="button" className="pp-comment-edit-btn" onClick={onEdit}>
            <Icon.Pencil size={12} /> {t('Edit')}
          </button>
          <button
            type="button"
            className="pp-comment-edit-btn pp-comment-delete-btn"
            onClick={() => setConfirming(true)}
          >
            <Icon.Trash size={12} /> {t('Delete')}
          </button>
        </>
      )}
      {editable && confirming && (
        <span className="pp-comment-confirm">
          {t('Delete this comment?')}
          <button
            type="button"
            className="pp-comment-edit-btn pp-comment-delete-btn"
            onClick={doDelete}
            disabled={deleting}
          >
            {t('Yes, delete')}
          </button>
          <button
            type="button"
            className="pp-comment-edit-btn"
            onClick={() => setConfirming(false)}
            disabled={deleting}
          >
            {t('Cancel')}
          </button>
        </span>
      )}
    </span>
  );
}

/**
 * Builds the Timeline item for a `Commented` activity entry — display view, inline editor,
 * deleted stub, byline with edit/delete, attachment strip and deep-link anchor — so every
 * document detail page renders comments identically.
 *
 * Args:
 *   activity: the Commented activity entry
 *   apiBasePath: document API path, e.g. `/requisitions/{id}`
 *   attachmentsBasePath: e.g. `/requisitions/{id}/attachments`
 *   currentUserId: for the edit/delete affordance (server re-checks)
 *   editingCommentId / setEditingCommentId: page-held "which comment is in edit mode" state
 *   onChanged: reload the activity feed after an edit or delete
 *   highlightId: from useCommentAnchor — flashes the deep-linked comment
 *   t: translation function from useT()
 */
export function buildCommentTimelineItem({
  activity: a,
  apiBasePath,
  attachmentsBasePath,
  currentUserId,
  editingCommentId,
  setEditingCommentId,
  onChanged,
  highlightId,
  t,
}) {
  const icon = <Icon.MessageSquare size={14} />;
  const meta = new Date(a.createdAt).toLocaleString();

  if (isCommentDeleted(a)) {
    return {
      id: a.id,
      tone: 'default',
      icon,
      title: <span id={`c-${a.id}`} className="pp-comment-deleted">{t('Comment removed')}</span>,
      meta,
    };
  }

  if (editingCommentId === a.id) {
    return {
      id: a.id,
      tone: 'default',
      icon,
      title: (
        <CommentEditor
          basePath={apiBasePath}
          activity={a}
          onSaved={() => { setEditingCommentId(null); onChanged?.(); }}
          onCancel={() => setEditingCommentId(null)}
        />
      ),
      meta,
    };
  }

  return {
    id: a.id,
    tone: 'default',
    icon,
    title: (
      <span id={`c-${a.id}`} className={highlightId === a.id ? 'pp-comment-flash' : undefined}>
        {a.summary
          ? <CommentText text={a.summary} mentions={commentMentions(a)} />
          : t('shared attachment(s)')}
      </span>
    ),
    meta,
    description: (
      <>
        <CommentByline
          activity={a}
          apiBasePath={apiBasePath}
          currentUserId={currentUserId}
          onEdit={() => setEditingCommentId(a.id)}
          onChanged={onChanged}
        />
        <CommentAttachments activity={a} attachmentsBasePath={attachmentsBasePath} />
      </>
    ),
  };
}
